import React, { useRef, useState, useCallback, useEffect, useMemo } from "react";
import { MoveLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dropdown, Button, Spinner, ToastContainer, Toast } from "react-bootstrap";
import DOMPurify from "dompurify";
import ErrorExtractor from "../common/error_extractor";
import CommonDropdown from "../common/common_dropDown";
import { submitPurchaseQuotationAPI, getPRDetailsApi, getVendorAddress } from "./pq-api";

import { Sidebar } from "../common/Sidebar";
import { Navbar } from "../common/Navbar";
import DatePicker from "react-datepicker";
import { Calendar } from "lucide-react";
import fileUploadIcon from "../../assets/svg/file_upload.svg";
import { FaTimes } from "react-icons/fa";

const CustomDateInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <button
    type="button"
    ref={ref}
    onClick={onClick}
    style={{
      color: "#262729",
      width: "100%",
      height: "40px",
      borderRadius: "12px",
      border: "1px solid #EBEBEB",
      paddingLeft: "16px",
      paddingRight: "16px",
    }}
    className="w-100 btn-border-grey form-control vehicle-form-input bg-white d-flex justify-content-between align-items-center gap-2"
  >
    {value ? value : <span style={{ color: "#8790A1" }}>{placeholder || "dd-mm-yyyy"}</span>}
    <Calendar size={16} />
  </button>
));
CustomDateInput.displayName = "CustomDateInput";

export const PurchaseQuotationCreate = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const adminUrl = process.env.REACT_APP_ADMIN_API_URL;

  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state || {};
  const from = location.state?.from;
  const handleBack = () => {
    if (from === "vendor") {
      navigate("/viewVendor", {
        state: {
          activeTab: "quotation",
          id: location.state?.vendorId,
        },
      });
    } else {
      navigate("/purchase-quotation");
    }
  };
  const [isLoader, setIsLoader] = useState(false);
  const vendorDetails = location.state;

  const rfqApiUrl = useMemo(() => {
    return `${apiUrl}get_purchase_request?status=Pending${vendorDetails?.vendorId ? `&vendor_id=${vendorDetails.vendorId}` : ""}`;
  }, [apiUrl, vendorDetails?.vendorId]);

  useEffect(() => {
    if (id != null) {
      getVendorEdit(id);
    }
  }, [id]);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  const [purchaseOrderOption, setPurchaseOrderOption] = useState(null);

  const [vendor, setVendor] = useState("");
  const [isIgst, setIsIgst] = useState(false);
  const [isCgst, setIsCgst] = useState(false);
  const [isSgst, setIsSgst] = useState(false);
  const [validityDate, setValidityDate] = useState();
  const [deliveryDate, setDeliveryDate] = useState();
  const [productList, setProductList] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState("");
  const [incoTerms, setIncoTerms] = useState("");
  const [noteInstructions, setNoteInstructions] = useState("");
  const [termConditions, setTermConditions] = useState("");
  const [authorizedSignature, setAuthorizedSignature] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [viewFiles, setViewFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = (files) => {
    const newFiles = files.map((file, index) => {
      const isImage = file.type.startsWith("image/");
      let preview = null;
      if (isImage) {
        preview = URL.createObjectURL(file);
      }
      return {
        id: Date.now() + index,
        file: file,
        name: file.name,
        preview: preview,
      };
    });
    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  const handleSelectFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,";
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      handleFiles(files);
    };
    input.click();
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const openFile = (url, name) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  useEffect(() => {
    if (vendorDetails) {
      setVendor(vendorDetails.vendorName);
    }
  }, [vendorDetails]);
  const getAddress = async (id) => {
    try {
      const response = await getVendorAddress(id);
      if (response.data.success === true) {
        setIsIgst(response.data.data.igst ?? false)
        setIsCgst(response.data.data.cgst ?? false)
        setIsSgst(response.data.data.sgst ?? false)
      }
    } catch (e) {

    }
  }
  const getPRDetails = async (id) => {
    try {
      const response = await getPRDetailsApi(id);

      if (response.data.success === true) {
        const poDetail = response.data.pr ?? {};
        setVendor(poDetail.vendor ?? "");

        const updatedData = poDetail.products.map((item) => ({
          ...item,
          status: "Pending",
          unit_without_tax: "",
          cgst: "",
          sgst: "",
          igst: "",
          tds: "",
        }));
        setProductList(updatedData ?? []);
        getAddress(response.data.pr.vendor_id)
      }
    } catch (err) {
      console.error("Error fetching category:", err);
    }
  };

  const [errorMsg, setErrorMsg] = useState({
    request_id: "",
    validity_date: "",
    delivery_date: "",
    products: "",
    payment_terms: "",
    inco_terms: "",
    note_instruction: "",
    term_conditions: "",
    authorized_signature: "",
  });
  const [groupedError, setGroupedError] = useState({});

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const submitForm = async () => {
    setIsLoader(true);
    const extractor = new ErrorExtractor();
    try {
      let base64File;
      if (uploadedFiles.length > 0) {
        base64File = await fileToBase64(uploadedFiles[0].file);
      }

      const preSanitizeData = {
        request_id: purchaseOrderOption != null ? purchaseOrderOption.value : "",
        validity_date: validityDate != null && validityDate !== "" ? validityDate.toLocaleDateString("en-CA") : "",
        delivery_date: deliveryDate != null && deliveryDate !== "" ? deliveryDate.toLocaleDateString("en-CA") : "",
        products: productList,
        payment_terms: paymentTerms,
        inco_terms: incoTerms,
        note_instruction: noteInstructions,
        term_conditions: termConditions,
        authorized_signature: base64File,
      };
      const sanitizedData = {};
      Object.entries(preSanitizeData).forEach(([key, value]) => {
        if (typeof value === "boolean" || typeof value === "number" || Array.isArray(value) || key === "assignee") {
          sanitizedData[key] = value;
        } else {
          sanitizedData[key] = DOMPurify.sanitize(value);
        }
      });
      var response = await submitPurchaseQuotationAPI(sanitizedData);

      console.log("sdddd", response);
      // Check if response indicates failure
      if (response.status === 400) {
        new Promise((resolve, reject) => {
          const groupedErrorTemp = {};
          Object.entries(response.data.error).forEach(([key, value]) => {
            // match tickets.index.field
            const match = key.match(/^products\.(\d+)\.(.+)$/);

            if (match) {
              const index = match[1]; // e.g. "0"
              const field = match[2]; // e.g. "closing_date"

              if (!groupedErrorTemp[index]) groupedErrorTemp[index] = {};
              groupedErrorTemp[index][field] = value;
            }
          });
          setGroupedError(groupedErrorTemp);
          const validationTemp = {
            request_id: "",
            validity_date: "",
            delivery_date: "",
            products: "",
            payment_terms: "",
            inco_terms: "",
            note_instruction: "",
            term_conditions: "",
            authorized_signature: "",
          };
          Object.entries(response.data.error).forEach(([key, value]) => {
            console.log(`${key}: ${value}`);
            validationTemp[key] = value.length === 0 ? "" : value[0];
          });
          console.log("validationTemp...", validationTemp);
          setErrorMsg(validationTemp);
        });
      } else if (response.data.success === true) {
        setSnackbarMessage(response.data.message);
        setSnackbarStatus(response.data.success);
        handleSnackbarShow();
        console.log("saveAttachment called 0");

        console.log("saveAttachment called 1");
        handleBack();
      }
    } catch (err) {
      console.error("Error submitting form:", err);

      // Optional: handle network errors separately
      if (err.response && err.response.status === 400 && err.response.data.error) {
        new Promise((resolve, reject) => {
          const groupedErrorTemp = {};
          Object.entries(err.response.data.error).forEach(([key, value]) => {
            // match tickets.index.field
            const match = key.match(/^products\.(\d+)\.(.+)$/);

            if (match) {
              const index = match[1]; // e.g. "0"
              const field = match[2]; // e.g. "closing_date"

              if (!groupedErrorTemp[index]) groupedErrorTemp[index] = {};
              groupedErrorTemp[index][field] = value;
            }
          });
          setGroupedError(groupedErrorTemp);
        });

        const errorvalue = extractor.extractErrors(err.response.data, {
          ...errorMsg,
        });
        setErrorMsg(errorvalue);
      }
    } finally {
      setIsLoader(false);
    }
  };

  const getVendorEdit = async (id) => {
    /*  try {
      const response = await getTransactionEditApi(id);

      if (response.data.success === true) {
        const vendorDetail = response.data.vendor ?? {};

        setVendorName(vendorDetail.company_name ?? "");
        setPhoneNumber(vendorDetail.mobile_number ?? "");
        setEmail(vendorDetail.email ?? "");
        setOwner(vendorDetail.owner ?? "");
        setStageOption({
          value: vendorDetail.stage ?? "",
          label: vendorDetail.stage ?? "",
        });
        setIndustryOption({
          value: vendorDetail.industry_id ?? "",
          label: vendorDetail.industry ?? "",
        });
        setSubIndustryOption({
          value: vendorDetail.sub_industry_id ?? "",
          label: vendorDetail.sub_industry ?? "",
        });
        setGradeOption({
          value: vendorDetail.grade_id ?? "",
          label: vendorDetail.grade ?? "",
        });
        setShipAddress(vendorDetail.ship_address ?? "");
        setShipCityOption({
          value: vendorDetail.ship_city_id ?? "",
          label: vendorDetail.ship_city ?? "",
        });
        setShipStateOption({
          value: vendorDetail.ship_state_id ?? "",
          label: vendorDetail.ship_state ?? "",
        });
        setShipCountryOption({
          value: vendorDetail.ship_country_id ?? "",
          label: vendorDetail.shipping_country ?? "",
        });

        setShipZipCode(vendorDetail.ship_zipcode);

        setBillAddress(vendorDetail.bill_address ?? "");
        setBillCityOption({
          value: vendorDetail.bill_city_id ?? "",
          label: vendorDetail.bill_city ?? "",
        });
        setBillStateOption({
          value: vendorDetail.bill_state_id ?? "",
          label: vendorDetail.billingstate ?? "",
        });
        setBillCountryOption({
          value: vendorDetail.bill_country_id ?? "",
          label: vendorDetail.bill_country ?? "",
        });

        setBillZipCode(vendorDetail.bill_zipcode);


      } else {
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
    } */
  };
  const getErrorMsg = (index, field) => {
    if (field === "product_id") {
      if (groupedError[index] != null) {
        if (groupedError[index].product_id != null) {
          return groupedError[index].product_id[0]?.replace(`products.${index}.product_id`, "product") ?? "";
        }
      }
    }

    if (field === "qty") {
      if (groupedError[index] != null) {
        if (groupedError[index].qty != null) {
          return groupedError[index].qty[0]?.replace(`products.${index}.qty`, "qty") ?? "";
        }
      }
    }
    if (field === "exception") {
      if (groupedError[index] != null) {
        if (groupedError[index].exception != null) {
          return groupedError[index].exception[0]?.replace(`products.${index}.exception`, "exception") ?? "";
        }
      }
    }
    if (field === "qty") {
      if (groupedError[index] != null) {
        if (groupedError[index].qty != null) {
          return groupedError[index].qty[0]?.replace(`products.${index}.qty`, "qty") ?? "";
        }
      }
    }

    if (field === "amount") {
      if (groupedError[index] != null) {
        if (groupedError[index].amount != null) {
          return groupedError[index].amount[0]?.replace(`products.${index}.amount`, "amount") ?? "";
        }
      }
    }

    if (field === "unit_without_tax") {
      if (groupedError[index] != null) {
        if (groupedError[index].unit_without_tax != null) {
          return groupedError[index].unit_without_tax[0]?.replace(`products.${index}.unit_without_tax`, "unit price") ?? "";
        }
      }
    }

    if (field === "tax_percentage") {
      if (groupedError[index] != null) {
        if (groupedError[index].tax_percentage != null) {
          return groupedError[index].tax_percentage[0]?.replace(`products.${index}.tax_percentage`, "tax percentage") ?? "";
        }
      }
    }

    return "";
  };

  const calculateTotalAmount = (row) => {
    const unitPrice = Number(row.unit_without_tax);
    const quantity = Number(row.qty);
    const cgst = Number(row.cgst);
    const sgst = Number(row.sgst);
    const igst = Number(row.igst);

    const baseAmount = unitPrice * quantity;
    const taxAmount = baseAmount * ((cgst + sgst + igst) / 100);

    return (baseAmount + taxAmount).toFixed(2);
  };

  return (
    <div className="d-flex flex-grow-1">
      <Sidebar currentfile="Purchase Quotation" />
      <div
        className="rounded-4 flex-grow-1 d-flex flex-column"
        style={{
          minWidth: 0,
          overflowX: "hidden",
        }}
      >
        <Navbar pageTitle="Quotation" />
        <div className="flex-grow-1" style={{ backgroundColor: "#F5F8FA" }}>
          <div className="bg-white px-3 py-2 d-flex align-items-center justify-content-between border-bottom" style={{ height: "64px" }}>
            <div className="d-flex gap-3 align-items-center">
              <MoveLeft
                size={20}
                className="cursor-pointer flex-shrink-0"
                onClick={handleBack}
              />
              <p className="fw-semibold m-0 fs-16">{id != null ? "Update quotation" : "New quotation"}</p>
            </div>
            <div className="d-flex gap-3">
              <Button
                className="cursor-pointer bg-white rounded-pill px-4 py-2 btn-border-grey fs-15 fw-500"
                style={{ border: "1px solid #D4DDE7", color: "#212129" }}
                onClick={handleBack}
              >
                Cancel
              </Button>
              <Button
                className={`cursor-pointer dark-purple-btn ${isLoader ? "disabled-btn" : ""} rounded-pill py-2 px-4 d-flex justify-content-center align-items-center`}
                onClick={isLoader ? null : submitForm}
                disabled={isLoader}
                style={{ minWidth: "100px" }}
              >
                {isLoader ? <Spinner animation="border" size="sm" /> : id != null ? "Update" : "Save"}
              </Button>
            </div>
          </div>

          <div className="m-2 bg-white rounded-4 p-3">
            <div className="row">
              <div className="col-12">
                <div className="row">
                  <div className="col-xl-12">
                    <p className="fs-16 fw-600 mb-0">Quotation Details</p>
                  </div>

                  <div className="mt-2 col-md-6 col-xl-3 col-12">
                    <label htmlFor="purchaseReq" className="form-label d-flex fw-400 my-0 mb-1">
                      RFQ <span className="error-text">*</span>
                    </label>
                    <div className="w-100">
                      <CommonDropdown
                        apiUrl={rfqApiUrl}
                        placeholder="Select"
                        value={purchaseOrderOption}
                        onSelect={(item) => {
                          setPurchaseOrderOption(item);
                          getPRDetails(item.value);
                        }}
                      />
                      {errorMsg.request_id && (
                        <span className="error-text fs-12 fw-400 d-block mt-1 ms-1">
                          {errorMsg.request_id}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 col-md-6 col-xl-3 col-12">
                    <label htmlFor="vendor" className="form-label fw-400 my-0 mb-1" style={{ width: "150px" }}>
                      Vendor
                    </label>
                    <div className="w-100">
                      <input
                        type="text"
                        className="form-control rounded-3 vendor-form-input"
                        id="vendor"
                        placeholder="Auto-Fill"
                        value={vendor || ""}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="mt-2 col-md-6 col-xl-3 col-12">
                    <label htmlFor="validityDate" className="form-label fw-400 my-0 d-flex mb-1" style={{ width: "150px" }}>
                      Validity Date <span className="error-text">*</span>
                    </label>
                    <div className="w-100">
                      <DatePicker
                        selected={validityDate}
                        onChange={(date) => setValidityDate(date)}
                        dateFormat="dd-MM-yyyy"
                        minDate={new Date()}
                        customInput={<CustomDateInput placeholder="dd-mm-yyyy" />}
                      />
                      {errorMsg.validity_date && (
                        <span className="error-text fs-12 fw-400 d-block mt-1 ms-1">
                          {errorMsg.validity_date}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 col-md-6 col-xl-3 col-12">
                    <label htmlFor="deliveryDate" className="form-label fw-400 my-0 d-flex mb-1" style={{ width: "150px" }}>
                      Delivery Date <span className="error-text">*</span>
                    </label>
                    <div className="w-100">
                      <DatePicker
                        selected={deliveryDate}
                        onChange={(date) => setDeliveryDate(date)}
                        dateFormat="dd-MM-yyyy"
                        minDate={new Date()}
                        customInput={<CustomDateInput placeholder="dd-mm-yyyy" />}
                      />
                      {errorMsg.delivery_date && (
                        <span className="error-text fs-12 fw-400 d-block mt-1 ms-1">
                          {errorMsg.delivery_date}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 col-md-6 col-xl-6 col-12 p-2">
                    <label htmlFor="paymentTerms" className="form-label fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                      Payment Terms
                    </label>
                    <div className="w-100 mt-2">
                      <textarea
                        className="form-control rounded-3 vendor-form-input"
                        id="paymentTerms"
                        placeholder="Enter here"
                        rows={2}
                        value={paymentTerms}
                        onChange={(e) => setPaymentTerms(e.target.value)}
                        style={{ overflowY: "auto", maxHeight: "150px", resize: "none", fontSize: "14px" }}
                      />
                      {errorMsg.payment_terms && (
                        <span className="error-text fs-12 fw-400 d-block mt-1 ms-1">
                          {errorMsg.payment_terms}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 col-md-6 col-xl-6 col-12 p-2">
                    <label htmlFor="incoTerms" className="form-label fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                      Inco Terms
                    </label>
                    <div className="w-100 mt-2">
                      <textarea
                        className="form-control rounded-3 vendor-form-input"
                        id="incoTerms"
                        placeholder="Enter here"
                        rows={2}
                        value={incoTerms}
                        onChange={(e) => setIncoTerms(e.target.value)}
                        style={{ overflowY: "auto", maxHeight: "150px", resize: "none", fontSize: "14px" }}
                      />
                      {errorMsg.inco_terms && (
                        <span className="error-text fs-12 fw-400 d-block mt-1 ms-1">
                          {errorMsg.inco_terms}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 col-md-6 col-xl-6 col-12 p-2">
                    <label htmlFor="noteInstructions" className="form-label fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                      Note & Instructions
                    </label>
                    <div className="w-100 mt-2">
                      <textarea
                        className="form-control rounded-3 vendor-form-input"
                        id="noteInstructions"
                        placeholder="Enter here"
                        rows={2}
                        value={noteInstructions}
                        onChange={(e) => setNoteInstructions(e.target.value)}
                        style={{ overflowY: "auto", maxHeight: "150px", resize: "none", fontSize: "14px" }}
                      />
                      {errorMsg.note_instruction && (
                        <span className="error-text fs-12 fw-400 d-block mt-1 ms-1">
                          {errorMsg.note_instruction}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 col-md-6 col-xl-6 col-12 p-2">
                    <label htmlFor="termConditions" className="form-label fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                      Term & Conditions
                    </label>
                    <div className="w-100 mt-2">
                      <textarea
                        className="form-control rounded-3 vendor-form-input"
                        id="termConditions"
                        placeholder="Enter here"
                        rows={2}
                        value={termConditions}
                        onChange={(e) => setTermConditions(e.target.value)}
                        style={{ overflowY: "auto", maxHeight: "150px", resize: "none", fontSize: "14px" }}
                      />
                      {errorMsg.term_conditions && (
                        <span className="error-text fs-12 fw-400 d-block mt-1 ms-1">
                          {errorMsg.term_conditions}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 col-12 p-2">
                    <label htmlFor="authorizedSignature" className="form-label fw-600 my-0 d-flex me-2 text-secondary mb-2" style={{ fontSize: "14px" }}>
                      Authorized Signature
                    </label>
                    <div className="w-100 mt-2">
                        {uploadedFiles.length > 0 && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              gap: "12px",
                              marginBottom: "16px",
                              overflowX: "auto",
                              paddingBottom: "8px",
                            }}
                          >
                            {uploadedFiles.map((file) => (
                              <div
                                key={file.id}
                                style={{
                                  position: "relative",
                                  width: "250px",
                                  height: "60px",
                                  borderRadius: "12px",
                                  overflow: "hidden",
                                  backgroundColor: "#F3F4F6",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openFile(file.preview, file.name ?? "");
                                }}
                              >
                                {file.preview ? (
                                  <img
                                    src={file.preview}
                                    alt={file.name ?? ""}
                                    style={{
                                      width: "100%",
                                      height: "60px",
                                      objectFit: "fit",
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#9CA3AF",
                                      fontSize: "14px",
                                    }}
                                  >
                                    No Preview
                                  </div>
                                )}
                                <FaTimes
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setUploadedFiles((prev) => prev.filter((f) => f.id !== file.id));
                                  }}
                                  style={{
                                    position: "absolute",
                                    top: "8px",
                                    right: "8px",
                                    fontSize: "18px",
                                    color: "#fff",
                                    background: "rgba(0,0,0,0.6)",
                                    borderRadius: "50%",
                                    padding: "4px",
                                    cursor: "pointer",
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        )}

                        {uploadedFiles.length === 0 && viewFiles.length === 0 && (
                          <div
                            onClick={handleSelectFile}
                            onDragOver={onDragOver}
                            onDragLeave={onDragLeave}
                            onDrop={onDrop}
                            style={{
                              height: "80px",
                              border: isDragging ? "2px dashed #5B50FE" : "1px solid #EBEBEB",
                              borderRadius: "12px",
                              padding: "12px",
                              textAlign: "center",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              backgroundColor: isDragging ? "#F3F4F6" : "transparent",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "12px",
                              opacity: 1
                            }}
                          >
                            <div
                              style={{
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <img src={fileUploadIcon} alt="Upload" width="40" height="40" />
                            </div>
                            <div style={{ textAlign: "left" }}>
                              <div className="fs-13 fw-400" style={{ color: "#374151", marginBottom: "2px" }}>
                                Drag and drop or{" "}
                                <span
                                  style={{
                                    color: "#5B50FE",
                                    cursor: "pointer",
                                    fontWeight: "500",
                                  }}
                                >
                                  Select a sign image
                                </span>
                              </div>
                              <div className="fs-12" style={{ color: "#9CA3AF" }}>
                                You can add image.
                              </div>
                            </div>
                          </div>
                        )}
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "start",
                          fontSize: "12px",
                          fontWeight: 400,
                          color: "#FF4444",
                          marginTop: "6px",
                          marginLeft: "6px",
                        }}
                      >
                        {errorMsg.authorized_signature}
                      </span>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

          <div className="m-2 bg-white rounded-4 p-3">
            <div className="row">
              <div className="col-12 ">
                <div className="row">
                  <div className="col-xl-12">
                    <p className="fs-18 fw-600 mb-0 d-flex">Product <span className="error-text">*</span></p>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "start",
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "#FF4444",
                        marginTop: "6px",
                        marginLeft: "6px",
                      }}
                    >
                      {errorMsg.request_id}
                    </span>
                  </div>
                  <div style={{ overflowY: "visible" }}>
                    <div className="mt-2 sticky-table-wrapper " style={{ minHeight: "auto" }}>
                      <table
                        className="sticky-table"
                        border="1"
                        cellPadding="10"
                        style={{
                          borderCollapse: "collapse",
                          width: "100%",
                        }}
                      >
                        <thead
                          style={{
                            height: "48px",
                            backgroundColor: "#F2F2F2", // ✅ Green background
                            color: "#212129", // ✅ White text
                            fontSize: "13px", // ✅ Font size
                            fontWeight: "700",
                            textAlign: "left",
                          }}
                        >
                          <tr>
                            <th className="ps-2">Product Name</th>
                            <th className="ps-2">Product ID</th>
                            <th className="ps-2">Unit</th>
                            <th className="ps-2"> Quantity</th>
                            <th className="ps-2"> Unit Price</th>
                            {isCgst && (<th className="ps-2"> CGST %</th>)}
                            {isIgst && (<th className="ps-2"> IGST %</th>)}
                            {isSgst && (<th className="ps-2"> SGST %</th>)}
                            <th className="ps-2"> Total Amount</th>
                            {/* <th className="ps-2"> TDS %</th> */}
                          </tr>
                        </thead>
                        <tbody>
                          {productList.map((row, index) => (
                            <tr key={row.id || index} style={{ height: "40px", position: "relative", overflow: "visible" }}>
                              <td className="px-2 py-1">
                                <div className="px-1 d-flex align-items-center" style={{ minWidth: "134px", height: "36px" }}>
                                  {row.name}
                                </div>
                              </td>
                              <td className="px-2 py-1">
                                <div className="px-1 d-flex align-items-center" style={{ minWidth: "134px", height: "36px" }}>
                                  {row.code}
                                </div>
                              </td>

                              <td className="px-2 py-1">
                                <div className="px-1 d-flex align-items-center" style={{ minWidth: "134px", height: "36px" }}>
                                  {row.unit}
                                </div>
                              </td>

                              <td className="px-2 py-1">
                                <div className="px-1 d-flex align-items-center" style={{ minWidth: "134px", height: "36px" }}>
                                  {row.qty}
                                </div>
                              </td>
                              <td className="px-2 py-1">
                                <div className="px-1" style={{ minWidth: "134px" }}>
                                  <input
                                    type="text"
                                    className="form-control rounded-3 vendor-form-input"
                                    placeholder="Enter price"
                                    value={row.unit_without_tax ?? ""}
                                    onChange={(e) => {
                                      const val = e.target.value.replace(/[^0-9.]/g, "");
                                      setProductList((prev) => {
                                        const updated = [...prev];
                                        updated[index] = { ...updated[index], unit_without_tax: val };
                                        return updated;
                                      });
                                    }}
                                  />
                                  <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index, "unit_without_tax")}</span>
                                </div>
                              </td>
                              {isCgst && (
                                <td className="px-2 py-1">
                                  <div className="" style={{ minWidth: "104px" }}>
                                    <input
                                      type="text"
                                      className="form-control rounded-3 vendor-form-input"
                                      placeholder="CGST %"
                                      value={row.cgst ?? ""}
                                      onChange={(e) => {
                                        let inputValue = e.target.value.replace(/[^0-9.]/g, "");
                                        let numericValue = parseFloat(inputValue);

                                        if (isNaN(numericValue)) {
                                          inputValue = "";
                                        } else if (numericValue > 100) {
                                          inputValue = "100"; // clamp to 100
                                        }
                                        setProductList((prev) => {
                                          const updated = [...prev];
                                          updated[index] = { ...updated[index], cgst: inputValue };
                                          return updated;
                                        });
                                      }}
                                    />
                                  </div>
                                </td>
                              )}
                              {isIgst && (
                                <td className="px-2 py-1">
                                  <div className="px-1" style={{ minWidth: "104px" }}>
                                    <input
                                      type="text"
                                      className="form-control rounded-3 vendor-form-input"
                                      placeholder="IGST %"
                                      value={row.igst ?? ""}
                                      onChange={(e) => {
                                        let inputValue = e.target.value.replace(/[^0-9.]/g, "");
                                        let numericValue = parseFloat(inputValue);

                                        if (isNaN(numericValue)) {
                                          inputValue = "";
                                        } else if (numericValue > 100) {
                                          inputValue = "100"; // clamp to 100
                                        }

                                        setProductList((prev) => {
                                          const updated = [...prev];
                                          updated[index] = { ...updated[index], igst: inputValue };
                                          return updated;
                                        });
                                      }}
                                    />
                                  </div>
                                </td>
                              )}
                              {isSgst && (
                                <td className="px-2 py-1">
                                  <div className="px-1" style={{ minWidth: "104px" }}>
                                    <input
                                      type="text"
                                      className="form-control rounded-3 vendor-form-input"
                                      placeholder="SGST %"
                                      value={row.sgst ?? ""}
                                      onChange={(e) => {
                                        let inputValue = e.target.value.replace(/[^0-9.]/g, "");
                                        let numericValue = parseFloat(inputValue);

                                        if (isNaN(numericValue)) {
                                          inputValue = "";
                                        } else if (numericValue > 100) {
                                          inputValue = "100"; // clamp to 100
                                        }

                                        setProductList((prev) => {
                                          const updated = [...prev];
                                          updated[index] = { ...updated[index], sgst: inputValue };
                                          return updated;
                                        });
                                      }}
                                    />
                                  </div>
                                </td>
                              )}
                              <td className="px-2 py-1">
                                <div
                                  className="mx-2 ps-2 d-flex align-items-center"
                                  style={{
                                    minWidth: "134px",
                                    height: "36px",
                                    borderRadius: "8px",
                                    background: "#F8F8F8",
                                    border: "1px solid #EBEBEB",
                                  }}
                                >
                                  {calculateTotalAmount(row)}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
